import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CountryDto } from '@app/data/dtos/geography/country-dto';
import { GeographicalBoundariesDto } from '@app/data/dtos/geography/geographical-boundaries.dto';
import { PhonePrefixDto } from '@app/data/dtos/geography/phone-prefix.dto';
import { StreetTypeDto } from '@app/data/dtos/geography/street-type.dto';
import {
  mapCountryData,
  mapGeographicalBoundariesData,
  mapPhonePrefixData,
  mapStreetTypeData
} from '@app/data/mappers/geography/geography.mapper';
import { GeographyRepository } from '@app/domain/repositories/geography/geography.repository.contract';
import { Country } from '@app/entities/models/geography/country.model';
import { GeographicalBoundary } from '@app/entities/models/geography/geography.model';
import { PhonePrefix } from '@app/entities/models/geography/phone-prefix.model';
import { StreetType } from '@app/entities/models/geography/street-type-model';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

@Injectable({
  providedIn: 'root'
})
export class GeographyRepositoryImpl extends MRepository implements GeographyRepository {
  constructor() {
    super(`${ENV.API_URL}`);
  }

  getCountries(): Observable<Country[]> {
    return this.get<CountryDto[]>(`/ptrab/geography/countries`, {
      context: new HttpContext().set(SECTION_KEY, 'personal-data-address')
    }).pipe(map(mapCountryData));
  }

  getPostalCodes(countryIsoNumericCode: string, alternativeCode: string): Observable<string[]> {
    let params = new HttpParams();
    params = params.set('country_iso_numeric_code', countryIsoNumericCode);
    params = params.set('alternative_code', alternativeCode);
    return this.get<MSafeAny>(`/ptrab/geography/postal-codes`, { params }).pipe(
      map((postalCodes) => postalCodes.postal_codes)
    );
  }

  getStreetTypes(): Observable<StreetType[]> {
    return this.get<StreetTypeDto[]>(`/ptrab/geography/street-types`).pipe(map(mapStreetTypeData));
  }

  getPhonePrefixes(): Observable<PhonePrefix[]> {
    return this.get<PhonePrefixDto[]>(`/ptrab/geography/phone-prefixes`).pipe(map(mapPhonePrefixData));
  }

  getGeographicalBoundaries(
    countryIsoNumericCode: string,
    alternativeCode?: string,
    parentTypeId?: string
  ): Observable<GeographicalBoundary[]> {
    let params = new HttpParams();
    params = params.set('country_iso_numeric_code', countryIsoNumericCode);

    if (parentTypeId && alternativeCode) {
      params = params.set('parent_type_id', parentTypeId);
      params = params.set('alternative_code', alternativeCode);
    }

    return this.get<GeographicalBoundariesDto[]>(`/ptrab/geography/geographical-boundaries`, { params }).pipe(
      map(mapGeographicalBoundariesData)
    );
  }
}
